import { useState, useEffect } from "react";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Users, TrendUp, CheckCircle, Clock, Warning } from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-md px-3 py-2 shadow-md text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          {p.name}: <span className="font-semibold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const CELL_COLORS = { high: "bg-emerald-500/80", mid: "bg-emerald-500/40", low: "bg-emerald-500/20", none: "bg-muted" };

export default function CohortAnalysisPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/analytics/cohorts")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load cohort data"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading cohort analysis...</div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">No data available</div>;

  const { cohorts, retention_matrix } = data;
  const totalSignups = cohorts.reduce((s, c) => s + c.signups, 0);
  const totalConverted = cohorts.reduce((s, c) => s + c.converted, 0);
  const avgRate = totalSignups > 0 ? (totalConverted / totalSignups * 100).toFixed(1) : 0;

  return (
    <div data-testid="cohort-analysis-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
          <Users size={18} className="text-white" weight="fill" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Cohort Analysis</h1>
          <p className="text-xs text-muted-foreground">Track signup cohorts and conversion over time</p>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-3 gap-3 mb-6" data-testid="cohort-kpis">
        <div className="border border-border rounded-lg p-4 bg-card">
          <Users size={18} className="text-blue-500 mb-1" weight="duotone" />
          <p className="text-2xl font-bold font-[Chivo]">{totalSignups}</p>
          <p className="text-xs text-muted-foreground">Total Signups (6mo)</p>
        </div>
        <div className="border border-border rounded-lg p-4 bg-card">
          <CheckCircle size={18} className="text-emerald-500 mb-1" weight="duotone" />
          <p className="text-2xl font-bold font-[Chivo]">{totalConverted}</p>
          <p className="text-xs text-muted-foreground">Converted</p>
        </div>
        <div className="border border-border rounded-lg p-4 bg-card">
          <TrendUp size={18} className="text-violet-500 mb-1" weight="duotone" />
          <p className="text-2xl font-bold font-[Chivo]">{avgRate}%</p>
          <p className="text-xs text-muted-foreground">Avg Conversion Rate</p>
        </div>
      </div>

      {/* Cohort Bar Chart */}
      <div className="border border-border rounded-lg p-4 bg-card mb-6" data-testid="cohort-chart">
        <h3 className="text-sm font-semibold font-[Chivo] mb-3">Monthly Cohorts</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={cohorts} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} className="fill-muted-foreground" />
            <YAxis tick={{ fontSize: 10 }} className="fill-muted-foreground" allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="signups" name="Signups" fill="#3B82F6" radius={[3, 3, 0, 0]} />
            <Bar dataKey="converted" name="Converted" fill="#10B981" radius={[3, 3, 0, 0]} />
            <Bar dataKey="active_trial" name="Active Trial" fill="#F59E0B" radius={[3, 3, 0, 0]} />
            <Bar dataKey="expired_trial" name="Expired" fill="#EF4444" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Cohort Details Table */}
      <div className="border border-border rounded-lg overflow-hidden mb-6" data-testid="cohort-table">
        <div className="px-4 py-3 bg-muted/30 border-b border-border">
          <h3 className="text-sm font-semibold font-[Chivo]">Cohort Breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border bg-muted/10">
                <th className="px-3 py-2 text-left font-semibold">Cohort</th>
                <th className="px-3 py-2 text-right font-semibold">Signups</th>
                <th className="px-3 py-2 text-right font-semibold">Converted</th>
                <th className="px-3 py-2 text-right font-semibold">Active Trial</th>
                <th className="px-3 py-2 text-right font-semibold">Expired</th>
                <th className="px-3 py-2 text-right font-semibold">Rate</th>
              </tr>
            </thead>
            <tbody>
              {cohorts.map(c => (
                <tr key={c.key} className="border-b border-border hover:bg-muted/10">
                  <td className="px-3 py-2 font-medium">{c.month}</td>
                  <td className="px-3 py-2 text-right">{c.signups}</td>
                  <td className="px-3 py-2 text-right text-emerald-600 font-medium">{c.converted}</td>
                  <td className="px-3 py-2 text-right text-amber-600">{c.active_trial}</td>
                  <td className="px-3 py-2 text-right text-red-500">{c.expired_trial}</td>
                  <td className="px-3 py-2 text-right">
                    <Badge className={`text-[9px] ${c.conversion_rate >= 50 ? "bg-emerald-100 text-emerald-700" : c.conversion_rate > 0 ? "bg-blue-100 text-blue-700" : "bg-muted text-muted-foreground"}`}>
                      {c.conversion_rate}%
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Retention Heatmap */}
      <div className="border border-border rounded-lg overflow-hidden" data-testid="retention-matrix">
        <div className="px-4 py-3 bg-muted/30 border-b border-border">
          <h3 className="text-sm font-semibold font-[Chivo]">Retention Matrix</h3>
          <p className="text-[10px] text-muted-foreground">Percentage of each cohort still active at month N</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="border-b border-border bg-muted/10">
                <th className="px-3 py-2 text-left font-semibold">Cohort</th>
                <th className="px-3 py-2 text-center font-semibold">Month 0</th>
                <th className="px-3 py-2 text-center font-semibold">Month 1</th>
                <th className="px-3 py-2 text-center font-semibold">Month 2</th>
                <th className="px-3 py-2 text-center font-semibold">Month 3</th>
                <th className="px-3 py-2 text-center font-semibold">Month 4</th>
                <th className="px-3 py-2 text-center font-semibold">Month 5</th>
              </tr>
            </thead>
            <tbody>
              {retention_matrix.map(row => (
                <tr key={row.cohort} className="border-b border-border">
                  <td className="px-3 py-2 font-medium">{row.cohort} <span className="text-muted-foreground">({row.signups})</span></td>
                  {[0, 1, 2, 3, 4, 5].map(n => {
                    const val = row[`month_${n}`];
                    const bg = val == null ? CELL_COLORS.none : val >= 70 ? CELL_COLORS.high : val >= 30 ? CELL_COLORS.mid : CELL_COLORS.low;
                    return (
                      <td key={n} className="px-3 py-2 text-center">
                        {val != null ? <span className={`inline-block px-2 py-0.5 rounded ${bg} text-white font-semibold`}>{val}%</span> : <span className="text-muted-foreground">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
